import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Trash2,
  Pill,
  Search,
  AlertTriangle,
  CheckCircle2,
  X,
  Layers,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { formatExpiryDate } from '@/utils/dateUtils';

/**
 * Single medicine searchable selector item.
 */
const MedicineCombobox = ({
  medicine,
  index,
  catalog = [],
  loadingCatalog = false,
  onSelectProduct,
  onSetManual,
  onClearSelection
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCatalog = useMemo(() => {
    if (!search.trim()) {
      return catalog.slice(0, 15);
    }
    const q = search.toLowerCase().trim();
    return catalog.filter((p) => {
      const nameMatch = p.name?.toLowerCase().includes(q);
      const typeMatch = p.type?.toLowerCase().includes(q);
      return nameMatch || typeMatch;
    }).slice(0, 20);
  }, [catalog, search]);

  const hasSelection = Boolean(medicine.name);
  const isErpSynced = Boolean(medicine.erp_product_id);

  if (hasSelection && !open) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Prescribed Medicine / Product</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch(medicine.name || '');
              setOpen(true);
            }}
            className="h-6 px-2 text-xs text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
          >
            Change Product
          </Button>
        </div>
        <div className="flex items-center justify-between gap-3 p-2.5 sm:p-3 rounded-lg border border-emerald-200/70 dark:border-emerald-900/50 bg-emerald-50/30 dark:bg-emerald-950/10">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0 text-emerald-700 dark:text-emerald-300">
              <Pill className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-xs sm:text-sm text-foreground truncate">
                {medicine.name}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                {medicine.type && (
                  <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-medium text-muted-foreground">
                    {medicine.type}
                  </span>
                )}
                {isErpSynced ? (
                  <>
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 font-medium py-0">
                      ERP Synced
                    </Badge>
                    {medicine.available_stock !== undefined && medicine.available_stock !== null && (
                      <span className={`text-[11px] font-medium ${medicine.available_stock > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600'}`}>
                        Avail: {medicine.available_stock} {medicine.dispensed_unit || 'units'}
                      </span>
                    )}
                  </>
                ) : (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 font-medium py-0">
                    Manual / Not Synced
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-md shrink-0"
            title="Clear medicine selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="space-y-1.5 relative">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">Search Synced Medicine / Product</Label>
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => {
            onSetManual(index, search || 'Custom Medicine');
            setOpen(false);
          }}
          className="h-5 p-0 text-[11px] text-muted-foreground hover:text-primary"
        >
          Enter manually instead
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={loadingCatalog ? 'Loading ERP catalog...' : 'Type medicine or formulation name...'}
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          className="pl-9 pr-8 h-9 text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl animate-in fade-in zoom-in-95">
          {loadingCatalog ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Loading synced medicines...
            </div>
          ) : filteredCatalog.length > 0 ? (
            <div className="divide-y divide-border">
              {filteredCatalog.map((product) => {
                const isOutOfStock = product.availableQuantity <= 0;
                return (
                  <div
                    key={product.erp_product_id ? `erp-${product.erp_product_id}` : `local-${product.id}`}
                    data-testid={`catalog-item-${product.erp_product_id || product.id}`}
                    onClick={() => {
                      onSelectProduct(index, product);
                      setOpen(false);
                      setSearch('');
                    }}
                    className="p-3 hover:bg-accent/60 cursor-pointer transition-colors flex items-center justify-between gap-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-foreground truncate">
                          {product.name}
                        </span>
                        {product.type && (
                          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground shrink-0">
                            {product.type}
                          </span>
                        )}
                        {product.erp_product_id ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300">
                            ERP Synced
                          </Badge>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className={`font-medium ${isOutOfStock ? 'text-destructive font-semibold' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {isOutOfStock ? 'Out of stock' : `Available: ${product.availableQuantity} ${product.unit}`}
                        </span>
                        {product.mrp > 0 && (
                          <span>₹{product.mrp.toFixed(2)}</span>
                        )}
                        {product.earliestExpiry && (
                          <span>Exp: {formatExpiryDate(product.earliestExpiry)}</span>
                        )}
                        {product.batches?.length > 1 && (
                          <span className="text-[11px] opacity-80">({product.batches.length} batches)</span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-7 text-xs text-primary font-medium"
                    >
                      Select
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-4 text-center space-y-2">
              <p className="text-xs text-muted-foreground">
                No matching product found in ERP catalog.
              </p>
            </div>
          )}

          {/* Option to add custom/manual medicine */}
          {search.trim() && (
            <div className="p-2.5 bg-muted/30 border-t border-border flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground truncate">
                Prescribe <strong className="text-foreground">"{search}"</strong> as uncataloged medicine?
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  onSetManual(index, search);
                  setOpen(false);
                  setSearch('');
                }}
                className="h-7 text-xs shrink-0"
              >
                Use as Manual Medicine
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const PrescriptionMedicineSection = ({
  medicines = [],
  onChange,
  optional = false,
  title = 'Medicines',
  inModal = false,
}) => {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  // Load synced inventory catalog on mount
  useEffect(() => {
    let isMounted = true;
    const fetchCatalog = async () => {
      try {
        setLoadingCatalog(true);
        const res = await api.get('/inventory');
        const items = res.data?.items || res.data || [];
        if (!isMounted) return;

        // Group / Aggregate items by ERP product ID or by item ID
        const productMap = new Map();
        (Array.isArray(items) ? items : []).forEach((item) => {
          const isOutOfStock = (item.status || '').toUpperCase() === 'OUT_OF_STOCK';
          if (isOutOfStock) return;

          const key = item.erp_product_id ? `erp_${item.erp_product_id}` : `item_${item.id}`;
          const qty = Number(item.quantity) || 0;
          const resQty = Number(item.reserved_quantity) || 0;
          const avail = Math.max(0, qty - resQty);

          if (!productMap.has(key)) {
            productMap.set(key, {
              id: item.id,
              erp_product_id: item.erp_product_id || null,
              name: item.name,
              type: item.type || 'Other',
              unit: item.unit || 'UNIT',
              mrp: Number(item.mrp) || 0,
              manufacturerName: item.manufacturerName || '',
              gst_percent: Number(item.gst_percent) || 0,
              item_code: item.item_code || '',
              totalQuantity: qty,
              totalReserved: resQty,
              availableQuantity: avail,
              earliestExpiry: item.expiryDate || null,
              batches: [item.erp_batch_no].filter(Boolean)
            });
          } else {
            const existing = productMap.get(key);
            existing.totalQuantity += qty;
            existing.totalReserved += resQty;
            existing.availableQuantity += avail;
            if (item.expiryDate && (!existing.earliestExpiry || item.expiryDate < existing.earliestExpiry)) {
              existing.earliestExpiry = item.expiryDate;
            }
            if (item.erp_batch_no && !existing.batches.includes(item.erp_batch_no)) {
              existing.batches.push(item.erp_batch_no);
            }
            if (!existing.gst_percent && item.gst_percent) {
              existing.gst_percent = Number(item.gst_percent) || 0;
            }
            if (!existing.item_code && item.item_code) {
              existing.item_code = item.item_code;
            }
          }
        });

        setCatalog(Array.from(productMap.values()));
      } catch (err) {
        console.warn('Failed to load clinic inventory for medicine selection:', err);
      } finally {
        if (isMounted) setLoadingCatalog(false);
      }
    };

    fetchCatalog();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddMedicine = () => {
    onChange([
      ...medicines,
      {
        name: '',
        dosage: '',
        instructions: '',
        frequency: '',
        duration: '',
        erp_product_id: null,
        dispensed_quantity: 1,
        dispensed_unit: 'UNIT',
        available_stock: null,
        is_manual: false
      }
    ]);
  };

  const handleRemoveMedicine = (index) => {
    const next = medicines.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleFieldChange = (index, field, value) => {
    const next = [...medicines];
    next[index] = {
      ...next[index],
      [field]: value,
    };
    onChange(next);
  };

  const handleSelectProduct = (index, product) => {
    // Check duplicate ERP product selection
    if (product.erp_product_id) {
      const isDuplicate = medicines.some(
        (m, i) => i !== index && Number(m.erp_product_id) === Number(product.erp_product_id)
      );
      if (isDuplicate) {
        toast.warning(`"${product.name}" is already in this prescription.`);
        return;
      }
    }

    const next = [...medicines];
    next[index] = {
      ...next[index],
      name: product.name,
      erp_product_id: product.erp_product_id || null,
      dispensed_unit: product.unit || next[index]?.dispensed_unit || 'UNIT',
      dispensed_quantity: Number(next[index]?.dispensed_quantity) > 0 ? Number(next[index].dispensed_quantity) : 1,
      available_stock: product.availableQuantity,
      type: product.type,
      mrp: product.mrp,
      is_manual: !product.erp_product_id
    };
    onChange(next);
  };

  const handleSetManual = (index, customName = '') => {
    const next = [...medicines];
    next[index] = {
      ...next[index],
      name: customName || next[index]?.name || '',
      erp_product_id: null,
      dispensed_unit: next[index]?.dispensed_unit || 'UNIT',
      dispensed_quantity: Number(next[index]?.dispensed_quantity) > 0 ? Number(next[index].dispensed_quantity) : 1,
      available_stock: null,
      is_manual: true
    };
    onChange(next);
  };

  const handleClearSelection = (index) => {
    const next = [...medicines];
    next[index] = {
      ...next[index],
      name: '',
      erp_product_id: null,
      available_stock: null,
      is_manual: false
    };
    onChange(next);
  };

  const medicinesList = (
    <div>
      {medicines.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-xl bg-muted/10">
          <Pill className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
          <p className="text-xs sm:text-sm text-muted-foreground mb-3 font-medium">No medicines added yet.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddMedicine}
            className="text-xs gap-1.5 h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add First Medicine
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {medicines.map((medicine, index) => {
            const reqQty = Number(medicine.dispensed_quantity) || 0;
            const availStock = medicine.available_stock;
            const isOverStock =
              medicine.erp_product_id !== null &&
              availStock !== undefined &&
              availStock !== null &&
              reqQty > availStock;

            return (
              <div
                key={index}
                className="p-3.5 sm:p-4.5 rounded-xl border border-border bg-card shadow-sm space-y-3.5 transition-all hover:border-slate-300 dark:hover:border-slate-700"
              >
                {/* Header Row */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 text-xs font-semibold">
                      {index + 1}
                    </span>
                    <span className="font-semibold text-xs sm:text-sm text-foreground">
                      Medicine #{index + 1}
                    </span>
                    {medicine.erp_product_id ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 font-medium">
                        ERP Synced
                      </Badge>
                    ) : medicine.name ? (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 font-medium">
                        Manual / Not Synced
                      </Badge>
                    ) : null}
                  </div>

                  {(medicines.length > 1 || optional) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMedicine(index)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md transition-colors"
                      title="Remove medicine line"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Combobox Search / Selector */}
                <MedicineCombobox
                  medicine={medicine}
                  index={index}
                  catalog={catalog}
                  loadingCatalog={loadingCatalog}
                  onSelectProduct={handleSelectProduct}
                  onSetManual={handleSetManual}
                  onClearSelection={() => handleClearSelection(index)}
                />

                {/* Stock Warning Banner */}
                {isOverStock && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-300 text-xs animate-in fade-in">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span>
                      Requested quantity (<strong>{reqQty}</strong>) exceeds current local available stock (<strong>{availStock} {medicine.dispensed_unit || 'units'}</strong>).
                    </span>
                  </div>
                )}

                {/* Structured Regimen & Dispensing Details Grid */}
                <div className="space-y-3 pt-1">
                  {/* Row 1: Dispensing & Regimen Details */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Quantity <span className="text-rose-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0.1"
                        step="any"
                        placeholder="e.g., 10"
                        value={medicine.dispensed_quantity !== undefined ? medicine.dispensed_quantity : 1}
                        onChange={(e) => handleFieldChange(index, 'dispensed_quantity', e.target.value)}
                        className="h-9 text-xs sm:text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Dispensed Unit
                      </Label>
                      <Input
                        placeholder="e.g., strip, bottle"
                        value={medicine.dispensed_unit || ''}
                        onChange={(e) => handleFieldChange(index, 'dispensed_unit', e.target.value)}
                        className="h-9 text-xs sm:text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Dosage
                      </Label>
                      <Input
                        placeholder="e.g., 500mg, 1 tablet"
                        value={medicine.dosage || ''}
                        onChange={(e) => handleFieldChange(index, 'dosage', e.target.value)}
                        className="h-9 text-xs sm:text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Frequency
                      </Label>
                      <Input
                        placeholder="e.g., Twice daily, OD"
                        value={medicine.frequency || ''}
                        onChange={(e) => handleFieldChange(index, 'frequency', e.target.value)}
                        className="h-9 text-xs sm:text-sm"
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-1 space-y-1">
                      <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Duration
                      </Label>
                      <Input
                        placeholder="e.g., 15 days, 1 month"
                        value={medicine.duration || ''}
                        onChange={(e) => handleFieldChange(index, 'duration', e.target.value)}
                        className="h-9 text-xs sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Row 2: Instructions (Full Width) */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Instructions / Intake Advice <span className="text-muted-foreground font-normal text-[11px]">(Optional)</span>
                    </Label>
                    <Input
                      placeholder="e.g., After food with warm water; avoid sour/cold foods"
                      value={medicine.instructions || ''}
                      onChange={(e) => handleFieldChange(index, 'instructions', e.target.value)}
                      className="h-9 text-xs sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (inModal) {
    return (
      <div className="space-y-3.5">
        <div className="flex items-center justify-between pb-2 border-b border-border/70">
          <div className="flex items-center gap-2">
            <Pill className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
            <div>
              <div className="font-serif text-base sm:text-lg font-bold text-foreground flex items-center gap-1.5">
                {title}
                {optional && <span className="text-xs font-normal text-muted-foreground font-sans">(Optional)</span>}
              </div>
              <p className="text-[11px] sm:text-xs text-muted-foreground">
                {optional
                  ? 'Transcribe items from prescription or select ERP-synced inventory.'
                  : 'Select medicines from synced ERP inventory or enter clinic formulations.'}
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleAddMedicine}
            className="text-xs gap-1.5 h-8 bg-emerald-700 hover:bg-emerald-800 text-white shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Medicine
          </Button>
        </div>

        {medicinesList}
      </div>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="font-serif text-xl">
                {title} {optional && <span className="text-xs font-normal text-muted-foreground ml-1">(Optional)</span>}
              </CardTitle>
              {optional ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Transcribe items from physical prescription or select ERP-synced medicines.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select medicines from synced ERP inventory or enter manual clinic formulations.
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleAddMedicine}
            className="text-xs gap-1.5 h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Medicine
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        {medicinesList}
      </CardContent>
    </Card>
  );
};

export default PrescriptionMedicineSection;